/**
 * Règles de validation du formulaire "Fichiers & Liens" (ressources partagées en live). Les bornes
 * fichier reproduisent exactement SharedResourceFileUploadInitInput.validate() côté back (Swagger) :
 * fileName ≤ 255 caractères, mimeType au format "type/sous-type", sizeBytes dans [1, 524288000].
 */

/** Même règle que la validation d'affichage : un lien doit être une URL http(s) absolue. */
export const isValidSharedResourceLinkUrl = (url: string): boolean =>
  /^https?:\/\/.+/i.test(url.trim());

/** Un lien est prêt à être envoyé quand titre et URL sont renseignés et l'URL est valide. */
export const isSharedResourceLinkFormValid = (title: string, url: string): boolean =>
  title.trim().length > 0 && isValidSharedResourceLinkUrl(url);

/** 500 Mo — reprend exactement la borne back (sizeBytes max 524288000). */
export const MAX_SHARED_RESOURCE_FILE_BYTES = 500 * 1024 * 1024;

const MAX_SHARED_RESOURCE_FILE_NAME_LENGTH = 255;
const SHARED_RESOURCE_MIME_TYPE_PATTERN = /^.+\/.+$/;

/** Le nom doit juste rester exploitable ; le format est contrôlé à part. */
export const isValidSharedResourceFileName = (fileName: string): boolean =>
  fileName.trim().length > 0 && fileName.length <= MAX_SHARED_RESOURCE_FILE_NAME_LENGTH;

/** Doit juste avoir la forme "type/sous-type" (ex. application/pdf, image/png) — aucun type exclu. */
export const isValidSharedResourceMimeType = (mimeType: string): boolean =>
  SHARED_RESOURCE_MIME_TYPE_PATTERN.test(mimeType);

/**
 * Formats diffusables aux spectateurs : PDF, images et CSV. Le back reste permissif, la restriction
 * vit ici. Le type déclaré par le navigateur n'est pas fiable (un .csv arrive souvent en
 * `application/vnd.ms-excel`, un fichier sans extension sans type du tout), donc le type OU
 * l'extension suffit à accepter.
 */
const ALLOWED_SHARED_RESOURCE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/csv',
];

const ALLOWED_SHARED_RESOURCE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'csv'];

/** Attribut `accept` du sélecteur de fichier, pour ne proposer que ces formats. */
export const SHARED_RESOURCE_FILE_ACCEPT = [
  ...ALLOWED_SHARED_RESOURCE_MIME_TYPES,
  ...ALLOWED_SHARED_RESOURCE_EXTENSIONS.map((extension) => `.${extension}`),
].join(',');

const normalizedMimeType = (mimeType: string): string =>
  mimeType.split(';')[0].trim().toLowerCase();

const fileExtension = (fileName: string): string => {
  const parts = fileName.trim().toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
};

export const isAllowedSharedResourceFileType = (file: { name: string; type: string }): boolean =>
  ALLOWED_SHARED_RESOURCE_MIME_TYPES.includes(normalizedMimeType(file.type)) ||
  ALLOWED_SHARED_RESOURCE_EXTENSIONS.includes(fileExtension(file.name));

/** Rejette les fichiers vides (0 octet, pas téléchargeable) et ceux dépassant la limite back. */
export const isValidSharedResourceFileSize = (sizeBytes: number): boolean =>
  sizeBytes >= 1 && sizeBytes <= MAX_SHARED_RESOURCE_FILE_BYTES;

export const isSharedResourceFileValid = (file: {
  name: string;
  type: string;
  size: number;
}): boolean =>
  isValidSharedResourceFileName(file.name) &&
  isValidSharedResourceMimeType(file.type) &&
  isAllowedSharedResourceFileType(file) &&
  isValidSharedResourceFileSize(file.size);
